import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbUser } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { LinearIcon } from "./linearIcon";
import { type LinearUsersPayload, parseLinearUsersPayload } from "./linearUsersSchema";

interface LinearUsersRendererProps {
  block: StreamingBlock;
}

export const LinearUsersRenderer = (props: LinearUsersRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<LinearUsersPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseLinearUsersPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("linear_users_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().users.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <LinearIcon class="size-4 shrink-0 text-primary-text" />
                <span class="truncate text-sm font-medium text-primary-text">{rt.t("linear_users_card.title")}</span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().users.length === 1
                    ? rt.t("linear_users_card.results_one")
                    : rt.t("linear_users_card.results", { count: String(data().users.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().users}>
                  {(user) => (
                    <li class="flex items-baseline gap-1.5 px-3 py-2">
                      <TbUser class="size-3.5 shrink-0 self-center text-muted-text" aria-hidden="true" />
                      <span class="truncate text-sm font-medium text-primary-text">{user.name}</span>
                      <span class="shrink-0 text-xs text-muted-text">@{user.displayName}</span>
                      <Show when={user.email}>
                        {(email) => <span class="truncate text-xs text-muted-text">{email()}</span>}
                      </Show>
                      <Show when={!user.active}>
                        <span class="ml-auto shrink-0 rounded bg-inset px-1.5 py-0.5 text-xs text-muted-text">
                          {rt.t("linear_users_card.inactive_badge")}
                        </span>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
